import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { SyncProvider } from './contexts/SyncContext';
import { TimezoneProvider } from './contexts/TimezoneContext';
import { ProtectedRoute, PublicRoute } from './components/ProtectedRoute';
import GmailTokenWarning from './components/GmailTokenWarning';
import SyncStatusBubble from './components/SyncStatusBubble';
import InterviewGenerationBubble from './components/InterviewGenerationBubble';
import { InterviewGenerationProvider } from './contexts/InterviewGenerationContext';
import LoginPage from './pages/auth/LoginPage';
import BusinessDetailsPage from './pages/account/BusinessDetailsPage'; // Unified component for create & update
import CredentialsPage from './pages/account/CredentialsPage';
import BillingPage from './pages/account/BillingPage';
import SavedLibraryPage from './pages/account/SavedLibraryPage';
import AccountSettingsPage from './pages/account/SettingsPage';
import MailSettingsPage from './pages/account/MailSettingsPage';
import ScoreConfigPage from './pages/account/ScoreConfigPage';
import SignupPage from './pages/auth/SignupPage';
import SettingsPage from './pages/settings/SettingsPage';
import DashboardPage from './pages/dashboard/DashboardPage';
import AccountLayout from './pages/account/AccountLayout';
import MasterDatabase from './pages/master-database/MasterDatabasePage';
import ChatPage from './pages/chat/ChatPage';
import ResumeParsingPage from './pages/resume-parsing/ResumeParsingPage';
import InterviewBookingPage from './pages/InterviewBookingPage';
import CandidateInterviewPage from './pages/CandidateInterviewPage';
import AIInterviewPage from './pages/ai-interview/AIInterviewPage';
import AIInterviewLaunchPage from './pages/ai-interview/AIInterviewLaunchPage';
import InterviewExperience from './pages/interview/InterviewExperience';
import CalendarCallbackPage from './pages/CalendarCallbackPage';
import GmailCallbackPage from './pages/GmailCallbackPage';
import OutlookCallbackPage from './pages/OutlookCallbackPage';
import NotificationsPage from './pages/notifications/NotificationsPage';
import ChangeMailPage from './pages/ChangeMailPage';
import RescheduleFormPage from './pages/RescheduleFormPage';
import RescheduleRequestsPage from './pages/RescheduleRequestsPage';
import LandingPage from './pages/LandingPage';
import WizboxLandingPage from './pages/WizboxLandingPage';
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminSitePage from './pages/admin/AdminSitePage';
import AdminAIConfigPage from './pages/admin/AdminAIConfigPage';
import AdminInterviewConfigPage from './pages/admin/AdminInterviewConfigPage';
import InterviewConfigurationPage from './pages/admin/InterviewConfigurationPage';
import MockInterviewConfigPage from './pages/admin/MockInterviewConfigPage';
import AdminPromptsPage from './pages/admin/AdminPromptsPage';
import AdminPromptsDetailPage from './pages/admin/AdminPromptsDetailPage';
import AdminMailTemplatesPage from './pages/admin/AdminMailTemplatesPage';
import AdminWorkflowDocumentsPage from './pages/admin/AdminWorkflowDocumentsPage';
import AdminLangfusePage from './pages/admin/AdminLangfusePage';
import AdminFeatureAccessPage from './pages/admin/AdminFeatureAccessPage';

function App() {
  return (
    <ThemeProvider>
      <SettingsProvider>
        <TimezoneProvider>
        <SyncProvider>
        <InterviewGenerationProvider>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <Routes>
            <Route path="/" element={<Navigate to="/landing" replace />} />

            {/* Public route - Landing page */}
            <Route path="/landing-v2" element={<LandingPage />} />
            <Route path="/landing" element={<WizboxLandingPage />} />

            {/* Public routes - redirect to dashboard if already authenticated */}
            <Route path="/auth/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
            <Route path="/auth/signup" element={<PublicRoute><SignupPage /></PublicRoute>} />

            {/* Public route - interview booking for candidates (no auth required) */}
            <Route path="/interviews/book/:token" element={<InterviewBookingPage />} />

            {/* Public route - AI interview for candidates (no auth required) */}
            <Route path="/interview/take/:token" element={<CandidateInterviewPage />} />

            {/* Public route - AI Interviewer chat (no auth required); optional prefill via path params */}
            <Route path="/ai-interview" element={<AIInterviewPage />} />
            <Route path="/ai-interview/:sessionId/:candidateId" element={<AIInterviewPage />} />
            <Route path="/ai-interview/voice" element={<InterviewExperience />} />
            <Route path="/ai-interview/voice/:sessionId/:candidateId" element={<InterviewExperience />} />
            <Route path="/ai-interview/launch/:token" element={<AIInterviewLaunchPage />} />

            {/* Public route - candidate reschedule request form (no auth required) */}
            <Route path="/reschedule-form/:token" element={<RescheduleFormPage />} />

            {/* Public route - Google Calendar OAuth callback (no auth required) */}
            <Route path="/calendar-callback" element={<CalendarCallbackPage />} />

            {/* Public route - Gmail OAuth callback (no auth required) */}
            <Route path="/gmail-callback" element={<GmailCallbackPage />} />

            {/* Public route - Outlook OAuth callback (no auth required) */}
            <Route path="/outlook-callback" element={<OutlookCallbackPage />} />

            {/* Dev utility - update candidate email after sourcing (no auth required) */}
            <Route path="/change-mail" element={<ChangeMailPage />} />

            {/* Workspace setup - requires auth, uses BusinessDetailsPage in create mode */}
            <Route path="/auth/workspace-setup" element={<ProtectedRoute><BusinessDetailsPage mode="create" /></ProtectedRoute>} />
            {/* Notifications page */}
            <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />

            {/* Credentials setup - appears after workspace setup (can be skipped) */}
            <Route path="/auth/credentials-setup" element={<ProtectedRoute><CredentialsPage isSetupMode={true} /></ProtectedRoute>} />
            
            {/* Protected routes - require auth + company profile (cid) */}
            <Route path="/reschedule-requests" element={<ProtectedRoute><RescheduleRequestsPage /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
            {/* Legacy: /settings redirects to /admin/settings */}
            <Route path="/settings" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
              <Route index element={<SettingsPage />} />
            </Route>
            <Route path="/master-database" element={<ProtectedRoute><MasterDatabase /></ProtectedRoute>} />
            <Route path="/resume-parsing" element={<ProtectedRoute><ResumeParsingPage /></ProtectedRoute>} />

            {/* Admin section with sidebar layout */}
            <Route path="/admin" element={<ProtectedRoute allowedRoles={[99]}><AdminLayout /></ProtectedRoute>}>
              <Route index element={<AdminDashboard />} />
              <Route path="ai-config" element={<AdminAIConfigPage />} />
              <Route path="interview-config" element={<AdminInterviewConfigPage />} />
              <Route path="interview-builder" element={<InterviewConfigurationPage />} />
              <Route path="mock-interview-config" element={<MockInterviewConfigPage />} />
              <Route path="prompts" element={<AdminPromptsPage />} />
              <Route path="prompts/:category" element={<AdminPromptsDetailPage />} />
              <Route path="mail-templates" element={<AdminMailTemplatesPage />} />
              <Route path="workflow-documents" element={<AdminWorkflowDocumentsPage />} />
              <Route path="feature-access" element={<AdminFeatureAccessPage />} />
              <Route path="llm-traces" element={<AdminLangfusePage />} />
              <Route path="usage" element={<AdminSitePage />} />
              <Route path="settings" element={<SettingsPage />} />
            </Route>
            {/* Legacy redirect */}
            <Route path="/admin-site" element={<ProtectedRoute allowedRoles={[99]}><AdminLayout /></ProtectedRoute>}>
              <Route index element={<AdminSitePage />} />
            </Route>
            <Route path="/notetaker-usage" element={<ProtectedRoute allowedRoles={[99]}><AdminSitePage usageSource="notetaker" /></ProtectedRoute>} />
            <Route path="/account" element={<ProtectedRoute><AccountLayout /></ProtectedRoute>}>

              <Route path="business-details" element={<BusinessDetailsPage mode="update" />} />
              <Route path="credentials" element={<CredentialsPage />} />
              <Route path="billing" element={<BillingPage />} />
              <Route path="saved-library" element={<SavedLibraryPage />} />
              <Route path="mail-settings" element={<MailSettingsPage />} />
              <Route path="interview-config" element={<InterviewConfigurationPage companyOnly />} />
              <Route path="settings" element={<AccountSettingsPage />} />
              <Route path="score-config" element={<ScoreConfigPage />} />
            </Route>
          </Routes>
          <GmailTokenWarning />
          <SyncStatusBubble />
          <InterviewGenerationBubble />
        </Router>
        </InterviewGenerationProvider>
        </SyncProvider>
        </TimezoneProvider>
      </SettingsProvider>
    </ThemeProvider>
  );
}

export default App;
